import React from "react";
import { Megaphone, Copy, ExternalLink } from "lucide-react";
import { BrandDetails } from "../../../../types/add-brand-types";
import { useMonitor } from "../../../../hooks/use-monitor";
import SocialSignalCard from "../../../widgets/SocialSignalCard";
import SocialSignalCardSkeleton from "../../../widgets/SocialSignalCardSkeleton";
import MonitoringSettings from "../../../modals/MonitoringSettings";
import Modal from "../../../uiComponents/Modal";
import "./monitor.css";
import Toast from "../../../uiComponents/Toast";

interface MonitorProps {
  brandDetails: BrandDetails;
  onUpdateKeywordClick: (keywords: string[], platforms: string[]) => void;
  refreshBrandDetails?: () => void;
}

const Monitor: React.FC<MonitorProps> = ({
  brandDetails,
  onUpdateKeywordClick,
  refreshBrandDetails,
}) => {
  const {
    socialSignals,
    isLoading,
    showMonitoringSettings,
    showConfirmDialog,
    handleDraftReply,
    handleMarkAsRepliedClick,
    handleConfirmMarkAsReplied,
    handleCancelMarkAsReplied,
    handleManageKeywords,
    handleCloseMonitoringSettings,
    toast,
    handleToastClose,
    draftRepliesPopup,
    closeDraftRepliesPopup,
    copyDraftReplyToClipboard,
    openDraftPostUrl,
    generatingDraftForSignalId,
  } = useMonitor(brandDetails, refreshBrandDetails);

  return (
    <div className="monitor-container">
      <MonitoringSettings
        isOpen={showMonitoringSettings}
        onClose={handleCloseMonitoringSettings}
        monitoringConfig={brandDetails.monitoring_config}
        onUpdateKeywordClick={onUpdateKeywordClick}
      />

      <Modal
        isOpen={showConfirmDialog}
        onClose={handleCancelMarkAsReplied}
        title="Mark as Replied"
        size="small"
        footer={
          <div
            style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}
          >
            <button
              onClick={handleCancelMarkAsReplied}
              style={{
                padding: "8px 16px",
                background: "white",
                color: "#6b7280",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmMarkAsReplied}
              style={{
                padding: "8px 16px",
                background: "#4f46e5",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              Confirm
            </button>
          </div>
        }
      >
        <p
          style={{
            margin: 0,
            color: "#374151",
            fontSize: "14px",
            lineHeight: "1.5",
          }}
        >
          Are you sure you want to mark this signal as replied? This action
          cannot be undone.
        </p>
      </Modal>

      <Modal
        isOpen={!!draftRepliesPopup}
        onClose={closeDraftRepliesPopup}
        title="Draft Replies"
        size="medium"
        footer={
          <div
            style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}
          >
            {draftRepliesPopup?.postUrl && (
              <button
                onClick={openDraftPostUrl}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 16px",
                  background: "#4f46e5",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
              >
                <ExternalLink size={16} />
                Open post
              </button>
            )}
            <button
              onClick={closeDraftRepliesPopup}
              style={{
                padding: "8px 16px",
                background: "white",
                color: "#6b7280",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        }
      >
        {draftRepliesPopup && (
          <div className="draft-replies-content">
            <div className="draft-reply-block">
              <label className="draft-reply-label">Reply 1</label>
              <p className="draft-reply-text">
                {draftRepliesPopup.replies.reply}
              </p>
              <button
                type="button"
                className="draft-reply-copy-btn"
                onClick={() =>
                  copyDraftReplyToClipboard(draftRepliesPopup.replies.reply)
                }
              >
                <Copy size={14} />
                Copy
              </button>
            </div>
            <div className="draft-reply-block">
              <label className="draft-reply-label">Reply 2</label>
              <p className="draft-reply-text">
                {draftRepliesPopup.replies.reply_2}
              </p>
              <button
                type="button"
                className="draft-reply-copy-btn"
                onClick={() =>
                  copyDraftReplyToClipboard(draftRepliesPopup.replies.reply_2)
                }
              >
                <Copy size={14} />
                Copy
              </button>
            </div>
          </div>
        )}
      </Modal>

      <div className="monitor-columns">
        <div className="monitor-column monitor-column--full">
          <div className="column-header">
            <div className="column-title">
              <Megaphone size={20} style={{ color: "#F97316" }} />
              <span>Social Signal Listener</span>
            </div>
            <button className="manage-btn" onClick={handleManageKeywords}>
              Manage Keywords
            </button>
          </div>

          <div className="cards-list cards-list--scrollable">
            {isLoading ? (
              <>
                <SocialSignalCardSkeleton />
                <SocialSignalCardSkeleton />
                <SocialSignalCardSkeleton />
              </>
            ) : socialSignals.length === 0 ? (
              <div className="monitor-empty-state">
                <Megaphone size={28} className="monitor-empty-icon" />
                <h4>No social signals yet</h4>
                <p>
                  We haven't found any matches for your keywords yet. Try adding
                  more keywords or platforms.
                </p>
              </div>
            ) : (
              socialSignals.map((signal) => (
                <SocialSignalCard
                  key={signal.id}
                  platform={
                    signal.platform as
                      | "reddit"
                      | "twitter"
                      | "linkedIn"
                      | "threads"
                      | "quora"
                  }
                  source={`${signal.platform}.com`}
                  timestamp={signal.created_at}
                  title={signal.data.title}
                  content={signal.data.content}
                  status={signal.status}
                  intent={"Discovery"}
                  url={signal.url}
                  onDraftReply={() => handleDraftReply(signal)}
                  onMarkAsReplied={() => handleMarkAsRepliedClick(signal.id)}
                  isDraftLoading={generatingDraftForSignalId === signal.id}
                />
              ))
            )}
          </div>
        </div>

        {/* Competitor review feed is intentionally kept out of the UI for now.
        <div className="monitor-column">
          ...
        </div>
        */}
      </div>
      {toast && toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={handleToastClose}
          duration={3000}
        />
      )}
    </div>
  );
};

export default Monitor;
